import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, Users, Calendar, TrendingUp, Activity } from 'lucide-react';
import { api } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Format large numbers
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

// Get color intensity based on value
const getHeatmapColor = (value, maxValue) => {
  if (value === 0) return 'bg-slate-100';
  const intensity = Math.min(value / Math.max(maxValue, 1), 1);
  if (intensity < 0.25) return 'bg-emerald-200';
  if (intensity < 0.5) return 'bg-emerald-400';
  if (intensity < 0.75) return 'bg-emerald-500';
  return 'bg-emerald-600';
};

// Get month name
const getMonthName = (monthIndex) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[monthIndex];
};

// Calendar Heatmap Component
const CalendarHeatmap = ({ data, year }) => {
  const [tooltip, setTooltip] = useState(null);
  
  // Get max value for color scaling
  const maxValue = Math.max(...data.map(d => d.page_views), 1);
  
  // Group data by date for quick lookup
  const dataMap = {};
  data.forEach(d => {
    dataMap[d.date] = d;
  });
  
  // Generate calendar grid
  const generateCalendarGrid = () => {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);
    const today = new Date();
    
    // Adjust to start from Sunday
    const startDay = startDate.getDay();
    const adjustedStart = new Date(startDate);
    adjustedStart.setDate(adjustedStart.getDate() - startDay);
    
    const weeks = [];
    let currentDate = new Date(adjustedStart);
    let currentWeek = [];
    
    while (currentDate <= endDate || currentWeek.length > 0) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const dayData = dataMap[dateStr] || { page_views: 0, unique_visitors: 0 };
      const isCurrentYear = currentDate.getFullYear() === year;
      const isFuture = currentDate > today;
      
      currentWeek.push({
        date: dateStr,
        dayOfMonth: currentDate.getDate(),
        month: currentDate.getMonth(),
        isCurrentYear,
        isFuture,
        ...dayData
      });
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
      
      // Stop if we've gone past the year and completed the week
      if (currentDate.getFullYear() > year && currentWeek.length === 0) {
        break;
      }
    }
    
    // Add remaining days if any
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    return weeks;
  };
  
  const weeks = generateCalendarGrid();
  
  // Get month labels positions
  const getMonthLabels = () => {
    const labels = [];
    let lastMonth = -1;
    
    weeks.forEach((week, weekIndex) => {
      const firstDayOfWeek = week.find(d => d.isCurrentYear);
      if (firstDayOfWeek && firstDayOfWeek.month !== lastMonth) {
        labels.push({ month: firstDayOfWeek.month, weekIndex });
        lastMonth = firstDayOfWeek.month;
      }
    });
    
    return labels;
  };
  
  const monthLabels = getMonthLabels();
  
  return (
    <div className="overflow-x-auto pb-4">
      {/* Month labels */}
      <div className="flex mb-2 text-xs text-slate-500 ml-8">
        {monthLabels.map((label, idx) => (
          <div 
            key={idx}
            style={{ marginLeft: idx === 0 ? 0 : `${(label.weekIndex - (monthLabels[idx-1]?.weekIndex || 0)) * 14 - 20}px` }}
          >
            {getMonthName(label.month)}
          </div>
        ))}
      </div>
      
      <div className="flex">
        {/* Day labels */}
        <div className="flex flex-col text-xs text-slate-500 mr-2 justify-around" style={{ height: '98px' }}>
          <span>Mon</span>
          <span>Wed</span>
          <span>Fri</span>
        </div>
        
        {/* Calendar grid */}
        <div className="flex gap-[3px]">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-[3px]">
              {week.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  className={`w-[12px] h-[12px] rounded-sm cursor-pointer transition-all hover:ring-2 hover:ring-slate-400 ${
                    !day.isCurrentYear || day.isFuture
                      ? 'bg-slate-50'
                      : getHeatmapColor(day.page_views, maxValue)
                  }`}
                  onMouseEnter={(e) => {
                    if (day.isCurrentYear && !day.isFuture) {
                      const rect = e.target.getBoundingClientRect();
                      setTooltip({
                        x: rect.left + window.scrollX,
                        y: rect.top + window.scrollY - 60,
                        date: day.date,
                        views: day.page_views,
                        visitors: day.unique_visitors
                      });
                    }
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      
      {/* Tooltip */}
      {tooltip && (
        <div 
          className="fixed z-50 bg-slate-900 text-white px-3 py-2 rounded-lg text-xs shadow-lg pointer-events-none"
          style={{ left: tooltip.x - 60, top: tooltip.y }}
        >
          <div className="font-semibold">{new Date(tooltip.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
          <div className="text-emerald-300">{tooltip.views} page views</div>
          <div className="text-blue-300">{tooltip.visitors} unique visitors</div>
        </div>
      )}
      
      {/* Legend */}
      <div className="flex items-center gap-2 mt-4 text-xs text-slate-500 ml-8">
        <span>Less</span>
        <div className="w-3 h-3 rounded-sm bg-slate-100"></div>
        <div className="w-3 h-3 rounded-sm bg-emerald-200"></div>
        <div className="w-3 h-3 rounded-sm bg-emerald-400"></div>
        <div className="w-3 h-3 rounded-sm bg-emerald-500"></div>
        <div className="w-3 h-3 rounded-sm bg-emerald-600"></div>
        <span>More</span>
      </div>
    </div>
  );
};

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  useEffect(() => {
    fetchStats();
    trackVisit();
  }, []);
  
  const trackVisit = async () => {
    try {
      await api.post('/analytics/track');
    } catch (err) {
      // Silently fail
    }
  };
  
  const fetchStats = async () => {
    try {
      const [statsRes, summaryRes] = await Promise.all([
        api.get('/analytics/stats?days=365'),
        api.get('/analytics/summary')
      ]);
      setStats(statsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1];
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="stats-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
        <div className="container-app py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Platform Statistics
            </h1>
            <p className="mt-4 text-slate-300 text-lg">
              Real-time visitor analytics for LegalHub
            </p>
          </motion.div>
        </div>
      </div>
      
      <div className="container-app py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Activity className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Today</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatNumber(summary?.today?.page_views || 0)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {summary?.today?.unique_visitors || 0} unique visitors
                    </p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">This Week</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatNumber(summary?.this_week?.page_views || 0)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {summary?.this_week?.unique_visitors || 0} unique visitors
                    </p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Eye className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">All Time</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatNumber(summary?.all_time?.page_views || 0)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {summary?.all_time?.unique_visitors || 0} unique visitors
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Calendar Heatmap */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-emerald-600" />
                  <h2 className="text-xl font-bold text-slate-900">Daily Activity</h2>
                </div>
                
                {/* Year Selector */}
                <div className="flex gap-2">
                  {years.map(year => (
                    <button
                      key={year}
                      onClick={() => setSelectedYear(year)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedYear === year
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
              
              {stats && (
                <CalendarHeatmap 
                  data={stats.daily_stats.filter(d => d.date.startsWith(selectedYear.toString()))} 
                  year={selectedYear}
                />
              )}
              
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatNumber(stats?.total_page_views || 0)}
                    </p>
                    <p className="text-sm text-slate-500">Total Page Views</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatNumber(stats?.total_unique_visitors || 0)}
                    </p>
                    <p className="text-sm text-slate-500">Unique Visitors</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {stats?.daily_stats?.filter(d => d.page_views > 0).length || 0}
                    </p>
                    <p className="text-sm text-slate-500">Active Days</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {Math.round((stats?.total_page_views || 0) / Math.max(stats?.daily_stats?.filter(d => d.page_views > 0).length || 1, 1))}
                    </p>
                    <p className="text-sm text-slate-500">Avg. Daily Views</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Info Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <Users className="w-6 h-6 text-emerald-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-emerald-800">About Our Analytics</h3>
                  <p className="text-sm text-emerald-700 mt-1">
                    We track page views and unique visitors to improve our platform. 
                    Visitor data is anonymized using secure hashing - we don't store personal information.
                    The heatmap shows daily activity with darker colors indicating higher traffic.
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default StatsPage;
